// src/hooks/useModuleFilters.js
import { useState, useCallback } from 'react';
import api from '../api/axiosConfig';

/**
 * Universal Hook for Module Filter Buttons & Panels
 * Automatically calls `GET /api/filters/:module` when the user clicks the [FILTERS] button
 *
 * @param {string} moduleName - The module identifier (e.g., 'tasks', 'inventory', 'workforce', 'vendors', 'equipment', 'assets', 'budget')
 * @param {object} defaultOptions - Initial fallback options before network response
 */
export const useModuleFilters = (moduleName, defaultOptions = {}) => {
  const [showFilter, setShowFilter] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState(defaultOptions);

  const toggleFilters = useCallback(async (forcedState) => {
    setShowFilter((prev) => {
      const next = typeof forcedState === 'boolean' ? forcedState : !prev;

      // When user clicks [FILTERS] to open, dispatch the API call to fetch filter dropdown data
      if (next && moduleName) {
        setLoadingFilters(true);
        api
          .get(`/filters/${moduleName}`)
          .then((res) => {
            if (res.data?.success && res.data?.data) {
              setFilterOptions(res.data.data);
            }
          })
          .catch((err) => {
            console.warn(`[useModuleFilters] Could not load filters for ${moduleName}:`, err);
          })
          .finally(() => {
            setLoadingFilters(false);
          });
      }

      return next;
    });
  }, [moduleName]);

  const refreshFilterOptions = useCallback(async () => {
    if (!moduleName) return;
    setLoadingFilters(true);
    try {
      const res = await api.get(`/filters/${moduleName}`);
      if (res.data?.success && res.data?.data) {
        setFilterOptions(res.data.data);
      }
    } catch (err) {
      console.warn(`[useModuleFilters] Refresh failed for ${moduleName}:`, err);
    } finally {
      setLoadingFilters(false);
    }
  }, [moduleName]);

  const [clearingFilters, setClearingFilters] = useState(false);

  const clearFilters = useCallback((onResetCallback) => {
    if (typeof onResetCallback === 'function') {
      onResetCallback();
    }
  }, []);

  return {
    showFilter,
    setShowFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
    refreshFilterOptions,
    clearFilters,
    clearingFilters,
  };
};

export default useModuleFilters;
